import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import useNewsAdminServer from "./useNewsAdminServer";
import { fetchAdminMetricsNotes } from "./api";
import { buildNewsPath } from "../data/noticias";

function formatViews(value) {
    return new Intl.NumberFormat("es-AR").format(Number(value || 0));
}

export default function FispTvAdminMetrics() {
    const { server, serverResolved } = useNewsAdminServer();
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("loading");
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        if (!serverResolved) {
            return undefined;
        }

        const controller = new AbortController();
        setStatus("loading");
        setPage(0);
        setHasMore(true);
        setLoadingMore(false);

        Promise.all([
            fetchAdminMetricsNotes(server, controller.signal, 1),
            fetchAdminMetricsNotes(server, controller.signal, 2),
        ])
            .then(([firstPage, secondPage]) => {
                const mergedItems = [];
                const seenKeys = new Set();

                [...firstPage, ...secondPage].forEach((item) => {
                    const itemKey = `${item.id}-${item.slug}-${item.title}`;

                    if (!seenKeys.has(itemKey)) {
                        seenKeys.add(itemKey);
                        mergedItems.push(item);
                    }
                });

                setItems(mergedItems);
                setPage(2);
                setHasMore(secondPage.length >= 20);
                setStatus("ready");
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }

                setItems([]);
                setPage(0);
                setHasMore(false);
                setStatus("error");
            });

        return () => controller.abort();
    }, [server, serverResolved]);

    const hasItems = useMemo(() => items.length > 0, [items]);

    const handleLoadMore = async () => {
        if (!hasMore || loadingMore) {
            return;
        }

        setLoadingMore(true);

        try {
            const nextPage = page + 1;
            const nextItems = await fetchAdminMetricsNotes(server, undefined, nextPage);

            setItems((currentItems) => {
                const mergedItems = [...currentItems];
                const seenKeys = new Set(currentItems.map((item) => `${item.id}-${item.slug}-${item.title}`));

                nextItems.forEach((item) => {
                    const itemKey = `${item.id}-${item.slug}-${item.title}`;

                    if (!seenKeys.has(itemKey)) {
                        seenKeys.add(itemKey);
                        mergedItems.push(item);
                    }
                });

                return mergedItems;
            });
            setPage(nextPage);
            setHasMore(nextItems.length >= 20);
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <AdminLayout title="Métricas" pageClassName="tv-admin-metrics-page">
            {status === "loading" ? (
                <div className="tv-admin-fullscreen-spinner" aria-live="polite" aria-busy="true">
                    <span className="tv-admin-spinner" aria-hidden="true"></span>
                </div>
            ) : null}

            {status === "error" ? (
                <section className="tv-admin-placeholder-card">
                    <h2>No pudimos cargar las métricas</h2>
                    <p>Probá nuevamente en unos minutos. El endpoint de vistas no respondió como esperábamos.</p>
                </section>
            ) : null}

            {status === "ready" && !hasItems ? (
                <section className="tv-admin-placeholder-card">
                    <h2>Sin datos por ahora</h2>
                    <p>Todavía no aparecieron notas publicadas con información de vistas en este listado.</p>
                </section>
            ) : null}

            {status === "ready" && hasItems ? (
                <section className="tv-admin-metrics-table" aria-label="Últimas noticias con vistas">
                    <div className="tv-admin-metrics-head" aria-hidden="true">
                        <span>Categoría</span>
                        <span>Título</span>
                        <span>Vistas</span>
                    </div>

                    <div className="tv-admin-metrics-list">
                        {items.map((item) => (
                            <Link
                                key={item.id || item.slug || item.title}
                                to={buildNewsPath(item.slug)}
                                className="tv-admin-metrics-card"
                            >
                                <span className="tv-admin-metrics-category">{String(item.category || "").replaceAll("-", " ")}</span>

                                <span className="tv-admin-metrics-copy">
                                    <strong>{item.title}</strong>
                                </span>

                                <span className="tv-admin-metrics-views">
                                    <strong>{formatViews(item.views)}</strong>
                                </span>
                            </Link>
                        ))}
                    </div>

                    {hasMore ? (
                        <div className="tv-admin-metrics-actions">
                            <button
                                type="button"
                                className="tv-admin-secondary-btn"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? "Cargando..." : "Ver más"}
                            </button>
                        </div>
                    ) : null}
                </section>
            ) : null}
        </AdminLayout>
    );
}
